/**
 * Date Formatting Utilities
 *
 * Centralized date formatting functions to ensure consistent date handling
 * across the application. Handles both ISO date strings and datetime strings.
 *
 * Expected input formats from backend:
 * - Date only: "YYYY-MM-DD" (e.g., "2024-01-15")
 * - DateTime: "YYYY-MM-DDTHH:mm:ss" or with timezone (e.g., "2024-01-15T14:30:00")
 *
 * Formatting follows the active UI locale (see i18n/intlTag); a caller can
 * still override the locale per call when needed.
 */

import { i18n, intlTag } from '../i18n';

export function formatDate(
    dateString: string | null | undefined,
    options: Intl.DateTimeFormatOptions | null = null,
): string {
    if (!dateString) return i18n.global.t('common.dates.unknown');

    try {
        let dateObj: Date;

        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            const [year, month, day] = dateString.split('-').map(Number);
            dateObj = new Date(year, month - 1, day);
        } else {
            dateObj = new Date(dateString);
        }

        if (isNaN(dateObj.getTime())) {
            console.warn(`Invalid date string: ${dateString}`);
            return i18n.global.t('common.dates.invalidDate');
        }

        const defaultOptions: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        };

        return dateObj.toLocaleDateString(intlTag(), options || defaultOptions);
    } catch (error) {
        console.error(`Error formatting date: ${dateString}`, error);
        return i18n.global.t('common.dates.invalidDate');
    }
}

export function formatDateTime(
    dateTimeString: string | null | undefined,
    options: Intl.DateTimeFormatOptions | null = null,
): string {
    if (!dateTimeString) return i18n.global.t('common.dates.unknown');

    try {
        const dateObj = new Date(dateTimeString);

        if (isNaN(dateObj.getTime())) {
            console.warn(`Invalid datetime string: ${dateTimeString}`);
            return i18n.global.t('common.dates.invalidDateTime');
        }

        const defaultOptions: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        };

        return dateObj.toLocaleDateString(intlTag(), options || defaultOptions);
    } catch (error) {
        console.error(`Error formatting datetime: ${dateTimeString}`, error);
        return i18n.global.t('common.dates.invalidDateTime');
    }
}

export function formatDateRange(
    startDate: string | null | undefined,
    endDate: string | null | undefined,
): string {
    const unknown = i18n.global.t('common.dates.unknown');
    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);

    if (formattedStart === unknown && formattedEnd === unknown) {
        return unknown;
    }

    if (formattedStart === unknown) {
        return i18n.global.t('common.dates.until', { date: formattedEnd });
    }

    if (formattedEnd === unknown) {
        return i18n.global.t('common.dates.from', { date: formattedStart });
    }

    return i18n.global.t('common.dates.range', { start: formattedStart, end: formattedEnd });
}

export function isValidDate(dateString: string | null | undefined): boolean {
    if (!dateString) return false;

    try {
        const dateObj = new Date(dateString);
        return !isNaN(dateObj.getTime());
    } catch {
        return false;
    }
}
